import { RefObject, useCallback, useMemo, useRef, useState } from 'react';

import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { ClearOutlined, SettingsOutlined } from '@mui/icons-material';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import { Box, Card, Skeleton, Typography } from '@mui/material';

import { Dialog, DialogButton } from '@/components/Dialog';
import presets from '@/components/Dialog/presets';
import TooltipIconButton from '@/components/TooltipIconButton';
import { useEditDashboardDraft } from '@/features/Dashboard/context/EditDashboardDraft';
import { DrawerStep, useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import { AppType, TileBigNumber } from '@/features/Dashboard/types';
import { convertTilesToActiveSymbols } from '@/features/Dashboard/utils/tileSymbolConverter';
import Tile from '@/pages/Markets/MarketsComponents/Tiles/Tile';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

import MoveTileToDashboardMenu from './MoveTileToDashboardMenu';

const TileSkeleton = () => (
  <Card sx={{ height: '56px', p: 0.25 }}>
    <Skeleton
      sx={{ height: '100%' }}
      variant='rectangular'
    />
  </Card>
);

const SortableTile = ({
  tile,
  onRequestRemove,
  onRequestEdit,
  index,
  container,
  currentDashboardName,
  currentTabName,
  onMoveToDraft,
  onRemoveDirectly
}: {
  index: number;
  tile: TileBigNumber;
  onRequestRemove: (id: string) => void;
  onRequestEdit: (id: string) => void;
  container: RefObject<HTMLDivElement | null>;
  currentDashboardName: string;
  currentTabName: string;
  onMoveToDraft: (tabName: string, tile: TileBigNumber) => void;
  onRemoveDirectly: (id: string) => void;
}) => {
  const { ref } = useSortable({
    id: tile.id,
    index,
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });

  const { preview } = useEditDashboardDraft();
  const [moveMenuAnchor, setMoveMenuAnchor] = useState<HTMLElement | null>(null);

  const content = useMemo(() => {
    const components = preview?.components ?? [];
    for (const app of components) {
      if ('component' in app && app.component?.tiles) {
        const found = app.component.tiles.find((t: any) => t.id === tile.id);
        if (found) return found;
      }
    }
    return undefined;
  }, [preview, tile.id]);

  return (
    <Box
      ref={ref}
      sx={{
        borderWidth: 2,
        borderStyle: 'dashed',
        borderColor: 'gx.textDisabled',
        display: 'flex',

        width: { xs: 'calc(100% - 16px)', sm: 'calc(50% - 16px)', lg: 'calc(25% - 16px)' },
        overflow: 'hidden',
        position: 'relative',
        pr: '60px',
        pl: '24px'
      }}
    >
      <TooltipIconButton
        sx={{
          cursor: 'grab',
          width: '24px !important',
          height: '100%',
          position: 'absolute',
          left: 0,
          top: 0
        }}
        title='Drag to reorder app'
        onClick={e => {
          e.stopPropagation();
          onRequestEdit(tile.id);
        }}
      >
        <DragIndicatorOutlinedIcon sx={{ fontSize: '21px !important' }} />
      </TooltipIconButton>

      <Box
        sx={{
          flexGrow: 1,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {content ? (
          <>
            <Tile tile={content} />
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                zIndex: 1,
                backgroundColor: 'rgba(18, 24, 54, 0.80)'
              }}
            />
          </>
        ) : (
          <TileSkeleton />
        )}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          display: 'flex',
          right: 0,
          top: 0,
          gap: 0.25,
          zIndex: 2,
          bottom: 0,
          backgroundColor: 'background.paper'
        }}
      >
        {/*<TooltipIconButton*/}
        {/*  sx={{*/}
        {/*    height: '100%'*/}
        {/*  }}*/}
        {/*  title='Move to ...'*/}
        {/*  onClick={e => {*/}
        {/*    e.stopPropagation();*/}
        {/*    setMoveMenuAnchor(e.currentTarget);*/}
        {/*  }}*/}
        {/*>*/}
        {/*  <HighlightAltOutlined sx={{ fontSize: '16px' }} />*/}
        {/*</TooltipIconButton>*/}
        <TooltipIconButton
          sx={{
            height: '100%'
          }}
          title='Configuration settings'
          onClick={e => {
            e.stopPropagation();
            onRequestEdit(tile.id);
          }}
        >
          <SettingsOutlined sx={{ fontSize: '16px' }} />
        </TooltipIconButton>

        <TooltipIconButton
          sx={{
            height: '100%'
          }}
          title='Remove item'
          onClick={e => {
            e.stopPropagation();
            onRequestRemove(tile.id);
          }}
        >
          <ClearOutlined
            sx={{
              fontSize: '16px'
            }}
          />
        </TooltipIconButton>
      </Box>
      <MoveTileToDashboardMenu
        anchorEl={moveMenuAnchor}
        currentDashboardName={currentDashboardName}
        currentTabName={currentTabName}
        open={Boolean(moveMenuAnchor)}
        tile={tile}
        onClose={() => {
          setMoveMenuAnchor(null);
        }}
        onMoveToDraft={onMoveToDraft}
        onRemoveFromCurrent={() => {
          onRemoveDirectly(tile.id);
        }}
      />
    </Box>
  );
};

export const TilesOrder = () => {
  const { dashboardName, currentTabName, setTabDrafts, setTiles, tabDrafts, tiles, setIsDragging } =
    useEditDashboardDraft();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);

  const { setDrawerStep, setIsEditMode, setEditingTarget } = useDrawerSteps();
  const { setInitialValues } = useAppForm();

  const requestRemove = (id: string) => {
    setPendingRemoveId(id);
    setConfirmOpen(true);
  };

  const requestEdit = useCallback(
    (id: string) => {
      setIsEditMode(true);
      setEditingTarget({ tileId: id });

      // Load existing tiles as active symbols
      const toActive = convertTilesToActiveSymbols(tiles);

      setInitialValues({
        type: AppType.Tiles,
        symbols: { active: toActive, backlog: [] }
      });

      setDrawerStep(DrawerStep.SelectHighlightedPrice);
    },
    [tiles, setEditingTarget, setIsEditMode, setInitialValues, setDrawerStep]
  );

  const handleRemove = (id: string) => {
    setTiles(items => items.filter(t => t.id !== id));
  };

  const handleMoveToDraft = useCallback(
    (tabName: string, tile: TileBigNumber) => {
      // Get the draft for the target tab, or create a new one
      const targetTabDraft = tabDrafts[tabName];

      // Convert tile to new ID
      const existingTileIds = new Set<string>();

      // Collect existing IDs from all drafts
      Object.values(tabDrafts).forEach(draft => {
        draft.tiles.forEach(t => {
          if (t.id) existingTileIds.add(t.id);
        });
      });

      const newTileId = generateUniqueUUID(existingTileIds);

      // Update the target tab's draft
      const updatedTargetDraft = {
        marketTabApps: targetTabDraft?.marketTabApps || [],
        tiles: targetTabDraft
          ? [...targetTabDraft.tiles, { ...tile, id: newTileId }]
          : [{ ...tile, id: newTileId }]
      };

      setTabDrafts(prev => ({
        ...prev,
        [tabName]: updatedTargetDraft
      }));
    },
    [tabDrafts, setTabDrafts]
  );

  const container = useRef<HTMLDivElement | null>(null);
  return (
    <DragDropProvider
      onDragEnd={event => {
        setTiles(items => move(items, event));
        setIsDragging(false);
      }}
      onDragStart={() => {
        setIsDragging(true);
      }}
    >
      <Box
        ref={container}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        {tiles.map((tile, index) => (
          <SortableTile
            key={tile.id}
            container={container}
            currentDashboardName={dashboardName}
            currentTabName={currentTabName}
            index={index}
            tile={tile}
            onMoveToDraft={handleMoveToDraft}
            onRemoveDirectly={handleRemove}
            onRequestEdit={requestEdit}
            onRequestRemove={requestRemove}
          />
        ))}

        <Dialog
          buttons={
            <>
              <DialogButton
                variant='outlined'
                onClick={() => {
                  setConfirmOpen(false);
                }}
              >
                {presets.removeDashboardItem.cancelText}
              </DialogButton>
              <DialogButton
                variant='contained'
                onClick={() => {
                  if (pendingRemoveId) {
                    handleRemove(pendingRemoveId);
                  }
                  setPendingRemoveId(null);
                  setConfirmOpen(false);
                }}
              >
                {presets.removeDashboardItem.confirmText}
              </DialogButton>
            </>
          }
          open={confirmOpen}
          title={presets.removeDashboardItem.title}
          onClose={() => {
            setConfirmOpen(false);
          }}
        >
          <Typography>{presets.removeDashboardItem.text}</Typography>
        </Dialog>
      </Box>
    </DragDropProvider>
  );
};
